import React, {useState} from 'react';
import Moment from 'moment';
import 'moment-timezone';
import NumberFormat from 'react-number-format';
import {Button, Spinner} from 'react-bootstrap';
import * as XLSX from 'xlsx';

const API_BASE_URL = 'https://europe-west3-digitalna-kartica.cloudfunctions.net';

/**
 * CustomDateReport component
 *
 * Allows users to generate reports for custom date ranges.
 * Currently configured for Gold Gondola.
 */
export const CustomDateReport = ({companyId}) => {
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [report, setReport] = useState(null);

    // Check if this is Gold Gondola
    const isGoldGondola = companyId === 'jLDiu0jZ8nVdWUi2sslOqB9dDrL2';

    const generateReport = async () => {
        if (!startDate || !endDate) {
            setError('Molimo izaberite početni i krajnji datum');
            return;
        }

        setLoading(true);
        setError(null);
        setReport(null);

        try {
            const response = await fetch(
                `${API_BASE_URL}/generateGondolaReport?key=dk-migration-secret-2024&startDate=${startDate}&endDate=${endDate}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Greška pri generisanju izveštaja');
            }

            const data = await response.json();
            setReport(data);
        } catch (err) {
            console.error('Error generating report:', err);
            setError(err.message || 'Došlo je do greške');
        } finally {
            setLoading(false);
        }
    };

    const exportToExcel = () => {
        if (!report) return;

        // Prepare data for Excel
        const summaryData = [
            ['Izveštaj za period', `${Moment(startDate).format('DD.MM.YYYY')} - ${Moment(endDate).format('DD.MM.YYYY')}`],
            ['Generisano', Moment(report.generatedAt).format('DD.MM.YYYY HH:mm')],
            [''],
            ['SUMARNI PODACI'],
            ['Metrika', 'Vrednost'],
            ['Broj novih korisnika (zapraćivanja)', report.summary.newUsersCount],
            ['Ukupno jedinstvenih korisnika u periodu', report.summary.uniqueUsersInPeriod],
            ['Ukupan broj skeniranja', report.summary.totalScansInPeriod],
            ['Ukupan iznos prodaje', report.summary.totalSalesValue],
            ['Ukupno izdatih vaučera', report.summary.totalVouchersIssued],
            ['Ukupno preuzetih vaučera', report.summary.totalVouchersUsed],
        ];

        // Vouchers by type
        const voucherTypeData = [
            [''],
            ['VAUČERI PO TIPU (kampanje kreirane u periodu)'],
            ['Tip', 'Opis', 'Izdato', 'Preuzeto', 'Stopa (%)'],
            ...(report.allVoucherTypes || []).map(v => [
                v.type,
                v.description,
                v.issued,
                v.used,
                v.usageRate
            ])
        ];

        // Points distribution
        // Combine all data
        const allData = [
            ...summaryData,
            ...voucherTypeData
        ];

        // Create workbook and worksheet
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.aoa_to_sheet(allData);

        // Set column widths
        ws['!cols'] = [
            {wch: 20},
            {wch: 45},
            {wch: 15},
            {wch: 15},
            {wch: 15}
        ];

        XLSX.utils.book_append_sheet(wb, ws, 'Izveštaj');

        // Generate filename
        const filename = `Gondola_Izvestaj_${Moment(startDate).format('DDMMYYYY')}_${Moment(endDate).format('DDMMYYYY')}.xlsx`;

        // Download
        XLSX.writeFile(wb, filename);
    };

    // Only show for Gold Gondola
    if (!isGoldGondola) {
        return null;
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-lg p-4">
                <h2 className="text-xl font-bold text-amber-800 mb-1">Izveštaj za proizvoljan period</h2>
                <p className="text-sm text-amber-600">Generišite izveštaj za željeni vremenski period</p>
            </div>

            {/* Date Selection */}
            <div className="bg-white p-4 rounded-lg shadow border">
                <div className="flex flex-wrap items-end gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Početni datum</label>
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                        />
                        {startDate && <div className="text-xs text-gray-500 mt-1">{Moment(startDate).format('DD.MM.YYYY')}</div>}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Krajnji datum</label>
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                        />
                        {endDate && <div className="text-xs text-gray-500 mt-1">{Moment(endDate).format('DD.MM.YYYY')}</div>}
                    </div>
                    <Button
                        variant="warning"
                        onClick={generateReport}
                        disabled={loading || !startDate || !endDate}
                        className="flex items-center gap-2"
                    >
                        {loading ? (
                            <>
                                <Spinner animation="border" size="sm" />
                                Generisanje...
                            </>
                        ) : (
                            'Generiši izveštaj'
                        )}
                    </Button>
                </div>

                {error && (
                    <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                        {error}
                    </div>
                )}
            </div>

            {/* Report Results */}
            {report && (
                <div className="space-y-6">
                    {/* Export Button */}
                    <div className="flex justify-end">
                        <Button variant="success" onClick={exportToExcel} className="flex items-center gap-2">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            Preuzmi Excel
                        </Button>
                    </div>

                    {/* Period Info */}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm">
                        <span className="font-medium">Period:</span> {Moment(startDate).format('DD.MM.YYYY')} - {Moment(endDate).format('DD.MM.YYYY')}
                        <span className="mx-3">|</span>
                        <span className="font-medium">Generisano:</span> {Moment(report.generatedAt).format('DD.MM.YYYY HH:mm')}
                    </div>

                    {/* Summary Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                        <div className="bg-white p-4 rounded-lg shadow border">
                            <div className="text-sm text-gray-500">Novi korisnici</div>
                            <div className="text-2xl font-bold text-green-600">
                                <NumberFormat value={report.summary.newUsersCount} displayType="text" thousandSeparator="." decimalSeparator="," />
                            </div>
                            <div className="text-xs text-gray-400">zapraćivanja</div>
                        </div>
                        <div className="bg-white p-4 rounded-lg shadow border">
                            <div className="text-sm text-gray-500">Jedinstvenih korisnika</div>
                            <div className="text-2xl font-bold text-blue-600">
                                <NumberFormat value={report.summary.uniqueUsersInPeriod} displayType="text" thousandSeparator="." decimalSeparator="," />
                            </div>
                            <div className="text-xs text-gray-400">u periodu</div>
                        </div>
                        <div className="bg-white p-4 rounded-lg shadow border">
                            <div className="text-sm text-gray-500">Broj skeniranja</div>
                            <div className="text-2xl font-bold text-purple-600">
                                <NumberFormat value={report.summary.totalScansInPeriod} displayType="text" thousandSeparator="." decimalSeparator="," />
                            </div>
                            <div className="text-xs text-gray-400">kupovina</div>
                        </div>
                        <div className="bg-white p-4 rounded-lg shadow border">
                            <div className="text-sm text-gray-500">Ukupna prodaja</div>
                            <div className="text-2xl font-bold text-amber-600">
                                <NumberFormat value={report.summary.totalSalesValue} displayType="text" thousandSeparator="." decimalSeparator="," suffix=" RSD" />
                            </div>
                            <div className="text-xs text-gray-400">iznos</div>
                        </div>
                        <div className="bg-white p-4 rounded-lg shadow border">
                            <div className="text-sm text-gray-500">Izdato vaučera</div>
                            <div className="text-2xl font-bold text-indigo-600">
                                <NumberFormat value={report.summary.totalVouchersIssued} displayType="text" thousandSeparator="." decimalSeparator="," />
                            </div>
                            <div className="text-xs text-gray-400">u periodu</div>
                        </div>
                        <div className="bg-white p-4 rounded-lg shadow border">
                            <div className="text-sm text-gray-500">Preuzeto vaučera</div>
                            <div className="text-2xl font-bold text-teal-600">
                                <NumberFormat value={report.summary.totalVouchersUsed} displayType="text" thousandSeparator="." decimalSeparator="," />
                            </div>
                            <div className="text-xs text-gray-400">u periodu</div>
                        </div>
                    </div>

                    {/* Vouchers by Type Table */}
                    {report.allVoucherTypes && report.allVoucherTypes.length > 0 && (
                        <div className="bg-white p-4 rounded-lg shadow border">
                            <h3 className="text-lg font-semibold mb-4">Vaučeri po tipu (kampanje kreirane u periodu)</h3>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tip</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Opis</th>
                                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Izdato</th>
                                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Preuzeto</th>
                                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Stopa (%)</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {report.allVoucherTypes.map((voucher, idx) => (
                                            <tr key={idx} className="hover:bg-gray-50">
                                                <td className="px-4 py-3 text-sm font-medium text-gray-900">{voucher.type}</td>
                                                <td className="px-4 py-3 text-sm text-gray-600">{voucher.description}</td>
                                                <td className="px-4 py-3 text-sm text-right text-blue-600 font-semibold">
                                                    <NumberFormat value={voucher.issued} displayType="text" thousandSeparator="." decimalSeparator="," />
                                                </td>
                                                <td className="px-4 py-3 text-sm text-right text-green-600 font-semibold">
                                                    <NumberFormat value={voucher.used} displayType="text" thousandSeparator="." decimalSeparator="," />
                                                </td>
                                                <td className="px-4 py-3 text-sm text-right font-medium text-purple-600">
                                                    {voucher.usageRate.toFixed(2)}%
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                </div>
            )}
        </div>
    );
};
